import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Info } from "lucide-react";
import { IMAGES, MENU_CATEGORIES, MENU_NOTE, img } from "../data/content";
import { Reveal } from "./Reveal";
import { Section } from "./Section";

const DishCard = ({ dish, index }) => (
  <motion.article
    data-testid={`menu-dish-${dish.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="card-surface group overflow-hidden"
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="relative overflow-hidden">
      <img
        src={img(IMAGES[dish.image], 700)}
        alt={`${dish.name} — plated café dish`}
        className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />
      <span className="absolute left-3 top-3 rounded-full bg-[#141210]/85 px-3 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-[#d4af37] backdrop-blur">
        Menu example
      </span>
    </div>
    <div className="p-5">
      <h3 className="font-display text-xl font-semibold text-[#f7f4ef]">{dish.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-[#b5afa7]">{dish.description}</p>
    </div>
  </motion.article>
);

export const MenuSection = () => {
  const [active, setActive] = useState(MENU_CATEGORIES[0].id);
  const category = MENU_CATEGORIES.find((c) => c.id === active);

  return (
    <Section
      id="menu"
      eyebrow="The Menu"
      title="Plates we're proud of"
      intro="A glimpse of the kitchen's style — small plates, diner classics, slow coffee and something sweet to finish."
    >
      <Reveal className="mb-8 flex items-start gap-3 rounded-xl border border-[#d4af37]/25 bg-[#d4af37]/5 p-4">
        <Info size={18} className="mt-0.5 shrink-0 text-[#d4af37]" aria-hidden="true" />
        <p className="text-sm leading-relaxed text-[#d8d2c9]" data-testid="menu-examples-note">
          {MENU_NOTE}
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div
          className="mb-10 flex flex-wrap gap-2"
          role="tablist"
          aria-label="Menu categories"
          data-testid="menu-tabs"
        >
          {MENU_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={active === c.id}
              data-testid={`menu-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`relative rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                active === c.id ? "text-[#141210]" : "text-[#b5afa7] hover:text-[#f7f4ef]"
              }`}
            >
              {active === c.id && (
                <motion.span
                  layoutId="menu-tab-pill"
                  className="absolute inset-0 rounded-full bg-[#d4af37]"
                  transition={{ type: "spring", stiffness: 320, damping: 30 }}
                />
              )}
              <span className="relative z-10">{c.label}</span>
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="menu-dish-grid"
        >
          {category.dishes.map((dish, i) => (
            <DishCard key={dish.name} dish={dish} index={i} />
          ))}
        </motion.div>
      </AnimatePresence>
    </Section>
  );
};
